import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  Calendar,
  Check,
  CircleDot,
  CornerDownRight,
  Diamond,
  Flag,
  Layers,
  Link2,
  Maximize2,
  Pencil,
  Shapes,
  Tag,
  Timer,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DetailColumns } from '@/components/ui/detail-columns';
import { EntitySelect } from '@/components/EntitySelect';
import { MultiPopover } from '@/components/ui/multi-popover';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { BlockContent, BlockEditor } from '@/components/ui/block-editor';
import { useSession } from '@/stores/session';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { CommentsPanel } from '@/features/comments';
import { CustomFieldsPanel } from '@/features/custom-fields';
import { useMembers } from '@/features/members';
import { uploadInlineImage } from '../api';
import { workItemPath } from '../paths';
import { LABEL_COLORS, PRIORITIES, type Priority } from '../constants';
import {
  cyclesHooks,
  labelsHooks,
  modulesHooks,
  projectMembersHooks,
  statesHooks,
  workItemTypesHooks,
  useDeleteWorkItem,
  useUpdateWorkItem,
  useWorkItem,
} from '../queries';
import { makeWorkItemSearch, resolveWorkItemLabel } from '../select';
import { formatEstimate, formatHours, parseEstimate } from '../timeFormat';
import { LoomEmbeds } from './LoomEmbeds';
import { WorkItemTimerButton } from './WorkItemTimerButton';
import { WorkItemRelations } from './WorkItemRelations';
import type {
  Cycle,
  Label as LabelModel,
  Module,
  Project,
  ProjectMember,
  State,
  WorkItem,
  WorkItemType,
} from '../types';
import { AssigneeAvatars } from './AssigneeAvatars';
import { MemberPicker } from './MemberPicker';
import { WorkItemTypeIcon } from './WorkItemTypeIcon';

function rows<T>(raw: { results: T[] } | T[] | undefined): T[] {
  return (Array.isArray(raw) ? raw : (raw?.results ?? [])) as T[];
}

/** Medium date + short time for the read-only created/updated footer. */
function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

interface DrawerProps {
  project: Project;
  workItemId: string | null;
  onClose: () => void;
}

export function WorkItemDetailDrawer({ project, workItemId, onClose }: DrawerProps) {
  const { data: item, isPending } = useWorkItem(workItemId ?? '');
  const navigate = useNavigate();
  const open = workItemId != null;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto p-6 sm:max-w-2xl lg:max-w-3xl">
        <SheetTitle className="sr-only">{item?.title ?? 'Work item'}</SheetTitle>
        {isPending || !item ? (
          <div className="space-y-4 p-2">
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : (
          // Keyed by id so local edit state initialises fresh per work item
          // (no setState-in-effect to sync props → state).
          <WorkItemForm
            key={item.id}
            project={project}
            item={item}
            onClose={onClose}
            onExpand={() => {
              onClose();
              navigate(workItemPath(project, item));
            }}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

export function WorkItemForm({
  project,
  item,
  onClose,
  onExpand,
  layout = 'drawer',
}: {
  project: Project;
  item: WorkItem;
  onClose: () => void;
  /** Drawer-only: open this item in full-page mode. */
  onExpand?: () => void;
  /** `drawer` (default) stacks everything in one column for the narrow Sheet;
   *  `page` uses the two-column DetailColumns layout with a Properties sidebar. */
  layout?: 'drawer' | 'page';
}) {
  const update = useUpdateWorkItem();
  const remove = useDeleteWorkItem();
  const [title, setTitle] = useState(item.title);
  const [editingTitle, setEditingTitle] = useState(false);
  // Title is view-first (rendered heading) with a pencil toggle into an editable,
  // wrapping, auto-growing textarea — same read/write modes as the description.
  // Grow to fit whenever the text changes or we enter edit mode.
  const titleRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
    // Focus the field when entering edit mode (cursor at end), without stealing
    // focus on every keystroke.
    if (editingTitle && document.activeElement !== el) {
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    }
  }, [title, editingTitle]);

  function commitTitle() {
    const next = title.trim();
    if (next && next !== item.title) patch({ title: next });
    else if (!next) setTitle(item.title); // titles can't be blank
    setEditingTitle(false);
  }
  // Description is view-first: read-only until the pencil toggles edit mode.
  const [editingDesc, setEditingDesc] = useState(false);
  // Guests may edit only with a member/lead project role (viewer = read-only);
  // staff always can. Worklog/relations/custom-field/comment panels are
  // staff-only endpoints, so they're hidden from guests entirely.
  const isGuest = useSession((s) => s.role) === 'guest';
  const canWrite = !isGuest || project.my_role === 'member' || project.my_role === 'lead';
  const isHours = project.estimate_type === 'hours';
  // Inline label create is open to any writer (staff + member/lead guests); the
  // BE gates it the same way (rename/delete stays admin-only in settings).
  const createLabel = labelsHooks.useCreate();

  // States are guest-readable; cycles/modules/labels/members are staff-only
  // endpoints — skip those queries for guests (their UI is hidden too).
  // These lists are now guest-readable, but only needed when the caller can
  // edit (the pickers are hidden otherwise) — so load them only for writers.
  const states = rows<State>(statesHooks.useList({ project: project.id }).data);
  // Guest-readable (like states) — fetched for everyone so the Type shows its
  // name in read-only mode too, not just for editors.
  const types = rows<WorkItemType>(
    workItemTypesHooks.useList({ project: project.id }).data,
  );
  const cycles = rows<Cycle>(cyclesHooks.useList({ project: project.id }, canWrite).data);
  const modules = rows<Module>(modulesHooks.useList({ project: project.id }, canWrite).data);
  const labels = rows<LabelModel>(labelsHooks.useList({ project: project.id }, canWrite).data);
  // Assignees come from the project's members only — not the whole org.
  const membersQuery = projectMembersHooks.useList({ project: project.id }, canWrite);
  const projectMembers = rows<ProjectMember>(membersQuery.data);
  // @mentions in comments are scoped to this project's members (not the whole
  // org). ProjectMember only carries the member id, so cross-reference the org
  // member list to resolve the full Member (needed for the user id in the token).
  const { data: orgMembersData } = useMembers({}, canWrite);
  const mentionMembers = useMemo(() => {
    const ids = new Set(projectMembers.map((pm) => pm.member));
    return (orgMembersData?.results ?? []).filter((m) => ids.has(m.id));
  }, [orgMembersData, projectMembers]);
  const milestones = project.milestones;
  const parentSearch = useMemo(
    () => makeWorkItemSearch(project.id, { excludeId: item.id }),
    [project.id, item.id],
  );

  function patch(input: Parameters<typeof update.mutate>[0]['input']) {
    update.mutate({ id: item.public_id, input });
  }

  function toggleArray(field: 'assignees' | 'labels', id: number) {
    const cur = item[field];
    patch({ [field]: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] });
  }

  // In the drawer (onExpand set) the Sheet's close button sits at top-4 right-4 —
  // reserve room so the timer button doesn't slide under it.
  const header = (
    <div className={`flex items-center gap-2 ${onExpand ? 'pr-10' : ''}`}>
      <WorkItemTypeIcon type={item.type_detail} className="size-4" />
      <Badge variant="outline" className="font-mono text-xs">
        {item.identifier}
      </Badge>
      {item.completed_at ? <Badge>Completed</Badge> : null}
      <Button
        variant="ghost"
        size="icon"
        className="size-7"
        aria-label="Copy link to this work item"
        title="Copy link"
        onClick={() => {
          const url = `${window.location.origin}${workItemPath(project, item)}`;
          void navigator.clipboard?.writeText(url);
          toast.success('Link copied');
        }}
      >
        <Link2 className="size-4" />
      </Button>
      {onExpand ? (
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          aria-label="Open full page"
          title="Open full page"
          onClick={onExpand}
        >
          <Maximize2 className="size-4" />
        </Button>
      ) : null}
      {!isGuest ? <WorkItemTimerButton item={item} /> : null}
    </div>
  );

  const titleBlock = (
    <div>
      <div className="flex items-start justify-between gap-2">
        {canWrite && editingTitle ? (
          <textarea
            ref={titleRef}
            value={title}
            rows={1}
            // Keep it a title: Enter commits (blur → save), Escape cancels;
            // strip any pasted line breaks.
            onChange={(e) => setTitle(e.target.value.replace(/\n/g, ''))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.currentTarget.blur();
              } else if (e.key === 'Escape') {
                setTitle(item.title);
                setEditingTitle(false);
              }
            }}
            onBlur={commitTitle}
            placeholder="Title"
            className="w-full resize-none overflow-hidden break-words border-0 bg-transparent px-0 text-xl font-bold leading-tight tracking-tight shadow-none outline-none focus-visible:ring-0 md:text-2xl"
          />
        ) : (
          <h2 className="w-full break-words text-xl font-bold leading-tight tracking-tight md:text-2xl">
            {item.title}
          </h2>
        )}
        {canWrite ? (
          <button
            type="button"
            aria-label={editingTitle ? 'Done editing title' : 'Edit title'}
            title={editingTitle ? 'Done' : 'Edit'}
            // While editing, keep focus on click so blur→commit fires once.
            onMouseDown={(e) => editingTitle && e.preventDefault()}
            onClick={() => (editingTitle ? commitTitle() : setEditingTitle(true))}
            className="text-muted-foreground hover:bg-muted hover:text-foreground mt-1 shrink-0 rounded p-1"
          >
            {editingTitle ? <Check className="size-4" /> : <Pencil className="size-3.5" />}
          </button>
        ) : null}
      </div>
      {item.logged_hours != null ? (
        <span className="text-muted-foreground mt-1.5 flex items-center gap-1 px-0 text-xs">
          <Timer className="size-3.5" /> Logged {formatHours(item.logged_hours)}
        </span>
      ) : null}
    </div>
  );

  const labelChips =
    item.label_details && item.label_details.length > 0 ? (
      // Selected labels as chips — visible to everyone (read-only viewers/guests
      // included). Writers get an × to remove the label from this item; the label
      // itself stays in the project (deleted only from Project Settings).
      <div className="flex flex-wrap items-center gap-1.5">
        {item.label_details.map((l) => (
          <span
            key={l.id}
            className="border-input inline-flex items-center gap-1.5 rounded-full border py-0.5 pl-2 pr-1 text-xs"
          >
            <span
              className="size-2 rounded-full"
              style={{ backgroundColor: l.color || 'var(--muted-foreground)' }}
            />
            {l.name}
            {canWrite ? (
              <button
                type="button"
                aria-label={`Remove label ${l.name}`}
                onClick={() => toggleArray('labels', l.id)}
                className="text-muted-foreground hover:bg-muted hover:text-foreground -mr-0.5 rounded-full p-0.5"
              >
                <X className="size-3" />
              </button>
            ) : (
              <span className="pr-1" />
            )}
          </span>
        ))}
      </div>
    ) : null;

  // Grouped, single-column properties — used by BOTH layouts (the drawer stacks
  // them under the title; the page puts them in the right-hand Properties card).
  // Semantic grouping (Details / Schedule / People / Structure) replaces the old
  // ragged 2-col grid. Selects/inputs are compact (h-8) so the rows read as a rail.
  const propertyGroups = (
    <div className="space-y-5">
      <PropGroup title="Details">
        <PropRow icon={Shapes} label="Type">
          <Select
            value={item.type ? String(item.type) : undefined}
            onValueChange={(v) => patch({ type: Number(v) })}
            disabled={!canWrite}
          >
            <SelectTrigger className="h-8">
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              {types.map((t) => (
                <SelectItem key={t.id} value={String(t.id)}>
                  <span className="flex items-center gap-2">
                    <WorkItemTypeIcon type={t} className="size-3.5" />
                    {t.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </PropRow>
        <PropRow icon={Activity} label="State">
          <Select
            value={item.state ? String(item.state) : undefined}
            onValueChange={(v) => patch({ state: Number(v) })}
            disabled={!canWrite}
          >
            <SelectTrigger className="h-8">
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              {states.map((s) => (
                <SelectItem key={s.id} value={String(s.id)}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </PropRow>
        <PropRow icon={Flag} label="Priority">
          <Select
            value={item.priority}
            onValueChange={(v) => patch({ priority: v as Priority })}
            disabled={!canWrite}
          >
            <SelectTrigger className="h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRIORITIES.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </PropRow>
      </PropGroup>

      <PropGroup title="Schedule">
        <PropRow icon={Calendar} label="Start date">
          <Input
            type="date"
            className="h-8"
            defaultValue={item.start_date ?? ''}
            onBlur={(e) => patch({ start_date: e.target.value || null })}
            disabled={!canWrite}
          />
        </PropRow>
        <PropRow icon={Calendar} label="Due date">
          <Input
            type="date"
            className="h-8"
            defaultValue={item.due_date ?? ''}
            onBlur={(e) => patch({ due_date: e.target.value || null })}
            disabled={!canWrite}
          />
        </PropRow>
        {/* Hours-mode stores whole minutes and shows/accepts "1h 30m"; points-mode
            is a plain number. Staff-only edit; read-only for guests. */}
        <PropRow icon={Timer} label={isHours ? 'Estimate' : 'Estimate (pts)'}>
          <Input
            key={item.estimate ?? 'empty'}
            type="text"
            className="h-8"
            inputMode={isHours ? 'text' : 'decimal'}
            defaultValue={formatEstimate(item.estimate, isHours)}
            onBlur={(e) => {
              const next = parseEstimate(e.target.value, isHours);
              const current = item.estimate == null ? null : Number(item.estimate);
              if (next !== current) patch({ estimate: next == null ? null : String(next) });
            }}
            disabled={!canWrite || isGuest}
            placeholder={isHours ? 'e.g. 1h 30m' : 'e.g. 3'}
          />
        </PropRow>
      </PropGroup>

      {/* People — writers get the assignee/label pickers; everyone sees the
          selected assignees (read-only) and label chips. */}
      <PropGroup title="People">
        {canWrite ? (
          <div className="flex flex-wrap items-center gap-2 py-1.5">
            <MemberPicker
              members={projectMembers}
              selected={item.assignees}
              onToggle={(id) => toggleArray('assignees', id)}
              onClear={() => patch({ assignees: [] })}
              known={item.assignee_details ?? []}
              loading={membersQuery.isLoading}
            />
            <MultiPopover
              icon={Tag}
              label="Labels"
              count={item.labels.length}
              options={labels.map((l) => ({ id: l.id, name: l.name, color: l.color }))}
              selected={item.labels}
              onToggle={(id) => toggleArray('labels', id)}
              onCreate={async (name) => {
                const color = LABEL_COLORS[labels.length % LABEL_COLORS.length];
                const created = await createLabel.mutateAsync({
                  project: project.id,
                  name,
                  color,
                });
                toggleArray('labels', created.id);
              }}
              creating={createLabel.isPending}
            />
          </div>
        ) : item.assignee_details && item.assignee_details.length > 0 ? (
          <PropRow icon={Users} label="Assignees">
            <span className="flex items-center gap-1.5 text-sm">
              <AssigneeAvatars assignees={item.assignee_details} max={3} />
              <span className="truncate">
                {item.assignee_details.map((a) => a.name).join(', ')}
              </span>
            </span>
          </PropRow>
        ) : null}
        {labelChips}
      </PropGroup>

      {/* Project structure — parent/cycle/module/milestone are WorkItem fields
          editable by writers; hidden from read-only viewers. */}
      {canWrite ? (
        <PropGroup title="Structure">
          <PropRow icon={CornerDownRight} label="Parent">
            <EntitySelect
              queryKey={`wi-parent-${item.id}`}
              value={item.parent ? String(item.parent) : ''}
              onChange={(v) => patch({ parent: v ? Number(v) : null })}
              search={parentSearch}
              resolveLabel={resolveWorkItemLabel}
              placeholder="Add parent"
              clearable
            />
          </PropRow>
          <PropRow icon={CircleDot} label="Cycle">
            <Select
              value={item.cycle ? String(item.cycle) : 'none'}
              onValueChange={(v) => patch({ cycle: v === 'none' ? null : Number(v) })}
            >
              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No cycle</SelectItem>
                {cycles.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </PropRow>
          <PropRow icon={Layers} label="Module">
            <Select
              value={item.module ? String(item.module) : 'none'}
              onValueChange={(v) => patch({ module: v === 'none' ? null : Number(v) })}
            >
              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No module</SelectItem>
                {modules.map((m) => <SelectItem key={m.id} value={String(m.id)}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </PropRow>
          <PropRow icon={Diamond} label="Milestone">
            <Select
              value={item.milestone ? String(item.milestone) : 'none'}
              onValueChange={(v) => patch({ milestone: v === 'none' ? null : Number(v) })}
            >
              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No milestone</SelectItem>
                {milestones.map((m) => <SelectItem key={m.id} value={String(m.id)}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </PropRow>
        </PropGroup>
      ) : null}
    </div>
  );

  const descriptionBlock = (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label>Description</Label>
        {canWrite ? (
          <button
            type="button"
            aria-label={editingDesc ? 'Done editing description' : 'Edit description'}
            title={editingDesc ? 'Done' : 'Edit'}
            onClick={() => setEditingDesc((v) => !v)}
            className="text-muted-foreground hover:bg-muted hover:text-foreground rounded p-1"
          >
            {editingDesc ? <Check className="size-4" /> : <Pencil className="size-3.5" />}
          </button>
        ) : null}
      </div>
      {canWrite && editingDesc ? (
        <BlockEditor
          compact
          placeholder="Add a description…"
          value={item.description_json}
          html={item.description}
          uploadImage={(file) => uploadInlineImage(project.id, file)}
          onSave={({ json, html }) => patch({ description: html, description_json: json })}
        />
      ) : item.description ? (
        <>
          <BlockContent value={item.description_json} html={item.description} />
          <LoomEmbeds html={item.description} />
        </>
      ) : canWrite ? (
        <button
          type="button"
          onClick={() => setEditingDesc(true)}
          className="text-muted-foreground hover:text-foreground text-sm"
        >
          Add a description…
        </button>
      ) : (
        <p className="text-muted-foreground text-sm">No description.</p>
      )}
    </div>
  );

  // Relations / custom fields / comments all use staff-only endpoints — hidden
  // from guests (they would 403).
  const collaboration = !isGuest ? (
    <>
      <div className="border-t pt-4">
        <WorkItemRelations item={item} project={project} />
      </div>
      <CustomFieldsPanel entity="projects.workitem" entityId={item.id} />
      <div className="border-t pt-4">
        <CommentsPanel
          modelLabel="projects.workitem"
          objectId={item.id}
          mentionMembers={mentionMembers}
        />
      </div>
    </>
  ) : null;

  const deleteBlock = canWrite ? (
    <div className="flex justify-end border-t pt-4">
      <Button
        variant="ghost"
        size="sm"
        className="text-destructive hover:text-destructive"
        onClick={() => {
          remove.mutate(item.public_id);
          onClose();
        }}
      >
        <Trash2 className="size-4" /> Delete
      </Button>
    </div>
  ) : null;

  const auditLine = (
    <p className="text-muted-foreground text-xs">
      Created {fmtDateTime(item.created)} · Updated {fmtDateTime(item.modified)}
    </p>
  );

  // Full-page: two-column DetailColumns (main content + Properties sidebar),
  // matching the CRM detail pages' convention.
  if (layout === 'page') {
    return (
      <>
        {header}
        <div className="mt-4">
          <DetailColumns
            main={
              <>
                {titleBlock}
                {descriptionBlock}
                {collaboration}
                {deleteBlock}
              </>
            }
            aside={
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Properties</CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  {propertyGroups}
                  <Separator />
                  {auditLine}
                </CardContent>
              </Card>
            }
          />
        </div>
      </>
    );
  }

  // Drawer: single column, everything stacked.
  return (
    <>
      {header}
      <div className="mt-2 space-y-6">
        {titleBlock}
        {propertyGroups}
        {descriptionBlock}
        {collaboration}
        {deleteBlock}
        <div className="border-t pt-3">{auditLine}</div>
      </div>
    </>
  );
}

function PropRow({
  icon: Icon,
  label,
  children,
}: {
  icon: typeof Users;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 py-1.5">
      <span className="text-muted-foreground flex w-32 shrink-0 items-center gap-2 text-sm">
        <Icon className="size-4" />
        {label}
      </span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

/** A labelled cluster of PropRows — the small uppercase heading gives the
 *  properties rail its "Details / Schedule / People / Structure" rhythm. */
function PropGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <p className="text-muted-foreground text-[11px] font-medium uppercase tracking-wide">
        {title}
      </p>
      <div>{children}</div>
    </div>
  );
}
